import './CurrentKnowledgeBasePage.css';
import { clsx } from 'clsx';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import CardContainer from 'src/components/CardContainer/CardContainer';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { useParams } from 'react-router-dom';
import { KNOWLEDGE_BASE_FILE_EXTENSIONS } from 'src/shared/constants';
import { CurrentKnowledgeBaseTableList } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseTableList/CurrentKnowledgeBaseTableList';
import { ExportKnowledgeBaseModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/ExportKnowledgeBaseModal/ExportKnowledgeBaseModal';
import { CurrentKnowledgeBaseToolbar } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseToolbar/CurrentKnowledgeBaseToolbar';
import { CurrentKnowledgeBaseBreadcrumb } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseBreadcrumb/CurrentKnowledgeBaseBreadcrumb';
import { useKBContext } from 'src/provider/KnowledgeBaseProvider';
import { CurrentKnowledgeBaseEditFilePopup } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseEditFilePopup/CurrentKnowledgeBaseEditFilePopup';
import { Separator } from 'src/components/ui/separator';
import { useKnowledgeBaseData } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/hooks/useKnowledgeBaseData';
import { useKnowledgeBaseTree } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/hooks/useKnowledgeBaseTree';
import { useKnowledgeBaseSelection } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/hooks/useKnowledgeBaseSelection';
import { useKnowledgeBaseFileActions } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/hooks/useKnowledgeBaseFileActions';
import { useKnowledgeBaseExport } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/hooks/useKnowledgeBaseExport';

export const CurrentKnowledgeBasePage = () => {
  const {
    tree,
    setTree,
    activeBranch,
    updateActiveBranch,
    setFilteredValue,
    setKnowledgeBaseName,
    skipRefetchFiles,
    setSkipRefetchFiles,
  } = useKBContext();
  const isLgDown = useMediaQuery('(max-width: 992px)');
  const { id: knowledgeBaseId } = useParams();

  const {
    currentKnowledgeBase,
    files,
    isLoadingFiles,
    isSuccessFiles,
    isActionButtonsDisabled,
    isCosupportManager,
    isV1,
    refetchFilesAndSync,
    getLazyKnowledgeBaseFiles,
    fetchFileById,
    addKnowledgeBaseFile,
    addKnowledgeBaseDocument,
    deleteKnowledgeBaseFiles,
    updateKnowledgeBaseFile,
    updateKnowledgeBaseFiles,
    integrations,
  } = useKnowledgeBaseData(knowledgeBaseId, skipRefetchFiles);

  const { onFileSearch } = useKnowledgeBaseTree({
    knowledgeBaseId,
    tree,
    setTree,
    activeBranch,
    updateActiveBranch,
    setFilteredValue,
    setKnowledgeBaseName,
    files,
    integrations,
    currentKnowledgeBase,
    skipRefetchFiles,
    isSuccessFiles,
    isV1,
    getLazyKnowledgeBaseFiles,
  });

  const {
    moveContentMode,
    deleteContentMode,
    checkedItemsCount,
    checkedFilesCount,
    onCheckedChange,
    handleSelectAllChange,
    resetCheckedItems,
    cancelCheckboxMode,
    setMoveContentMode,
    setDeleteContentMode,
  } = useKnowledgeBaseSelection({ tree, activeBranch, updateActiveBranch });

  const {
    dragStarted,
    onDragEnter,
    onDragOver,
    onDragLeave,
    handleDrop,
    onFileChange,
    fileInputValue,
    inputFileRef,
    handleAddFolderItem,
    addKnowledgeBaseSource,
    onAddKnowledgeBaseSourceOpen,
    handleItemsDelete,
    handleMoveItems,
  } = useKnowledgeBaseFileActions({
    knowledgeBaseId,
    tree,
    activeBranch,
    updateActiveBranch,
    skipRefetchFiles,
    setSkipRefetchFiles,
    addKnowledgeBaseFile,
    addKnowledgeBaseDocument,
    deleteKnowledgeBaseFiles,
    updateKnowledgeBaseFile,
    updateKnowledgeBaseFiles,
    refetchFilesAndSync,
    cancelCheckboxMode,
    resetCheckedItems,
  });

  const { exportAllFiles } = useKnowledgeBaseExport({
    files,
    knowledgeBaseName: currentKnowledgeBase?.name,
    fetchFileById,
  });

  return (
    <PageContentWrapper className={clsx('knowledge-base-page-container', !isLgDown && 'relative')}>
      <CurrentKnowledgeBaseEditFilePopup />
      <div className="z-10 gap-[10px] flex flex-col relative h-[100%]">
        <div className={'flex flex-row justify-between'}>
          <CurrentKnowledgeBaseBreadcrumb />
          <div className={'relative flex flex-row gap-2'}>
            {isCosupportManager && isV1 && (
              <ExportKnowledgeBaseModal
                className={clsx(Boolean(moveContentMode || deleteContentMode) && 'hidden')}
                handleExport={exportAllFiles}
              />
            )}
          </div>
        </div>
        <CardContainer className="h-[100%] min-w-[500px]">
          <CurrentKnowledgeBaseToolbar
            moveContentMode={moveContentMode}
            deleteContentMode={deleteContentMode}
            checkedItemsCount={checkedItemsCount}
            checkedFilesCount={checkedFilesCount}
            handleSelectAllChange={handleSelectAllChange}
            cancelCheckboxMode={cancelCheckboxMode}
            setMoveContentMode={setMoveContentMode}
            setDeleteContentMode={setDeleteContentMode}
            handleAddFolderItem={handleAddFolderItem}
            addKnowledgeBaseSource={addKnowledgeBaseSource}
            onAddKnowledgeBaseSourceOpen={onAddKnowledgeBaseSourceOpen}
            handleItemsDelete={handleItemsDelete}
            handleMoveItems={handleMoveItems}
            isActionButtonsDisabled={isActionButtonsDisabled}
            onFileSearch={onFileSearch}
          />
          <Separator className={'bg-light mt-2'} />

          <section
            className={clsx(dragStarted && 'drag-active', 'overflow-hidden')}
            onDrop={handleDrop}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
          >
            <input
              type="file"
              ref={inputFileRef}
              className={'hidden'}
              onChange={onFileChange}
              multiple={true}
              accept={KNOWLEDGE_BASE_FILE_EXTENSIONS.filter((ext) => ext !== '.faq').join(', ')}
              value={fileInputValue}
            />
            {activeBranch && (
              <CurrentKnowledgeBaseTableList
                onCheckedChange={onCheckedChange}
                handleItemsDelete={handleItemsDelete}
                handleMoveItems={handleMoveItems}
                isLoadingFiles={isLoadingFiles}
                deleteContentMode={deleteContentMode}
                moveContentMode={moveContentMode}
              />
            )}
          </section>
        </CardContainer>
      </div>
    </PageContentWrapper>
  );
};
